/**
 * Encryption business logic operations - separated from React state
 */

import { checkEncryptionHealth } from '@/lib/encryption-health-check';
import type { EncryptionState } from './types';

/**
 * Default encryption state
 */
export const getDefaultEncryptionState = (): EncryptionState => ({
  isEncryptionReady: false,
  isCheckingEncryption: false,
  encryptionError: null
});

/**
 * State when user is not authenticated
 */
export const getUnauthenticatedState = (): EncryptionState => ({
  isEncryptionReady: false,
  isCheckingEncryption: false,
  encryptionError: null
});

/**
 * State while checking encryption
 */
export const getCheckingState = (): Partial<EncryptionState> => ({
  isCheckingEncryption: true
});

/**
 * State when encryption is ready
 */
export const getReadyState = (): EncryptionState => ({
  isEncryptionReady: true,
  isCheckingEncryption: false,
  encryptionError: null
});

/**
 * State when encryption check failed
 */
export const getFailedState = (errorMessage: string): EncryptionState => ({
  isEncryptionReady: false,
  isCheckingEncryption: false,
  encryptionError: errorMessage
});

/**
 * Perform encryption health check
 * @param retries Number of retries for the health check
 * @returns Result with success status and optional error message
 */
export const performEncryptionHealthCheck = async (
  retries: number = 3
): Promise<{ success: boolean; errorMessage?: string }> => {
  console.log('[Encryption] Starting encryption health check...');
  
  try {
    const isHealthy = await checkEncryptionHealth(retries);
    
    if (isHealthy) {
      console.log('[Encryption] ✅ Encryption is ready');
      return { success: true };
    } else {
      console.error('[Encryption] ❌ Encryption health check failed');
      return { 
        success: false, 
        errorMessage: 'Sistema de criptografia indisponível. Verifique sua conexão e tente novamente.' 
      };
    }
  } catch (error) {
    console.error('[Encryption] ❌ Exception during encryption check:', error);
    return { 
      success: false, 
      errorMessage: 'Erro ao verificar criptografia' 
    };
  }
};
